import { z } from 'zod';
import { backgroundImages } from '../types/Images';
import { InputSpec as FilterSpecInput, ModuleSpec as FilterSpecModule, GroupSpec, ThemeSpec as FilterSpecTheme, } from './FilterTypesSpec';
import { IconSpec } from './IconSpec';
export var ThemeSpec = FilterSpecTheme.extend({
    id: z.string().nonempty(),
});
export var InputSpec = FilterSpecInput.extend({
    macroName: z.string().nonempty(),
    default: z.any().optional(),
}).catchall(z.any());
export var ModuleSpec = FilterSpecModule.extend({
    id: z.string().nonempty(),
    inputs: z.array(InputSpec).default([]),
    groups: z.array(GroupSpec).default([]),
    rs2f: z.string(),
});
export var BooleanInputDefaultSpec = z.boolean().optional().default(false);
// Concrete implementations for each input type
export var BooleanInputSpec = InputSpec.extend({
    type: z.literal('boolean'),
    default: BooleanInputDefaultSpec,
});
export var NumberInputDefaultSpec = z.number().optional();
export var NumberInputSpec = InputSpec.extend({
    type: z.literal('number'),
    default: NumberInputDefaultSpec,
});
export var StringListInputDefaultSpec = z
    .array(z.string())
    .optional()
    .default([]);
export var StringListInputSpec = InputSpec.extend({
    type: z.literal('stringlist'),
    default: StringListInputDefaultSpec,
});
export var ListOptionSpec = z.object({
    label: z.string(),
    value: z.string(),
});
export var EnumListInputDefaultSpec = z
    .array(z.string())
    .optional()
    .default([]);
export var EnumListInputSpec = InputSpec.extend({
    type: z.literal('enumlist'),
    default: EnumListInputDefaultSpec,
    enum: z.array(ListOptionSpec.or(z.string())).min(1),
});
export var ArgbHexColorSpec = z
    .string()
    .startsWith('#')
    .min(7)
    .max(9)
    .optional()
    .catch('#FF000000');
export var StyleConfigSpec = z.object({
    hidden: z.boolean().optional(),
    textColor: ArgbHexColorSpec.optional(),
    backgroundColor: ArgbHexColorSpec.optional(),
    borderColor: ArgbHexColorSpec.optional(),
    textAccent: z.number().min(1).max(4).optional(),
    textAccentColor: ArgbHexColorSpec.optional(),
    fontType: z.number().min(1).max(3).optional(),
    showLootbeam: z.boolean().optional(),
    lootbeamColor: ArgbHexColorSpec.optional(),
    showValue: z.boolean().optional(),
    showDespawn: z.boolean().optional(),
    notify: z.boolean().optional(),
    hideOverlay: z.boolean().optional(),
    highlightTile: z.boolean().optional(),
    menuTextColor: ArgbHexColorSpec.optional(),
    tileStrokeColor: ArgbHexColorSpec.optional(),
    tileFillColor: ArgbHexColorSpec.optional(),
    tileHighlightColor: ArgbHexColorSpec.optional(),
    sound: z.string().or(z.number()).optional(),
    menuSort: z.number().optional(),
    icon: IconSpec.optional(),
});
export var StyleInputSpec = InputSpec.extend({
    type: z.literal('style'),
    default: StyleConfigSpec.optional(),
    backgroundImage: z
        .enum(backgroundImages)
        .optional(),
    exampleItem: z.string().optional(),
    exampleItemId: z.number().optional(),
    disableDisplayMode: z.boolean().optional(),
});
export var TextInputDefaultSpec = z.string().optional().default('');
export var TextInputSpec = InputSpec.extend({
    type: z.literal('text'),
    default: TextInputDefaultSpec,
});
export var FilterSpec = z.object({
    id: z.string().nonempty(),
    name: z.string().nonempty(),
    description: z.string().optional(),
    rs2fHash: z.string(),
    active: z.boolean().default(false),
    importedOn: z.string().datetime().default(new Date().toISOString()),
    updatedOn: z.string().datetime().optional(),
    source: z.string().url().optional(),
    commit: z
        .string()
        .regex(/^[a-f0-9]{40}$/i)
        .optional(),
    revisionUrl: z.string().url().optional(),
    modules: z.array(ModuleSpec),
    themes: z.array(ThemeSpec).default([]),
    rs2f: z.string(),
});
export var ListDiffSpec = z.object({
    added: z.array(z.string()),
    removed: z.array(z.string()),
});
export var DEFAULT_FILTER_CONFIGURATION = {
    enabledModules: {},
    inputConfigs: {},
};
export var FilterConfigurationSpec = z.object({
    enabledModules: z.record(z.string(), z.boolean()).optional().default({}),
    inputConfigs: z.record(z.string(), z.any()).optional().default({}),
    prefixRs2f: z.string().optional(),
    suffixRs2f: z.string().optional(),
    selectedThemeId: z.string().optional(),
});
export var isEnumListInput = function (v) {
    return v.type === 'enumlist';
};
